import React from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { CrossSellListProps } from "./crossSellList.model";
import { RequestProposalCard } from "@companion-core/shared/app/Components/Requests/RequestProposalCard";
import { serviceIcons } from "@companion-core/shared/app/Constants/myRequests";
import { Button } from "@companion-core/shared/app/Components/Button";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import {
  trackClickCrossSellLater,
  trackClickCrossSellBack,
  trackClickCrossSellCCS,
  trackClickCrossSellGCCS,
} from "@companion-core/shared/app/tracking/crossSell";

import "@companion-core/web/src/Assets/Styles/scss/crossSell.scss";
import { getCloudinaryOptimizedUrl } from "@companion-core/shared/app/Utils/cloudinary";
import { useBellLink } from "@companion-core/shared/app/Hooks/useBellLink";

export const CrossSellList = ({
  data,
  animated = true,
  onCancel,
  requestId,
  canGoBack,
  title,
}: CrossSellListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openBellLink } = useBellLink({ platform: "web", bellReferrer: "cross-sell" });

  return (
    <div className="cross-sell__list" data-testid="cross-sell__list">
      {data && (
        <div className={animated ? "cross-sell__list--animated" : ""}>
          <div className="cross-sell__list-wrapper">
            {canGoBack ? (
              <button
                className="cross-sell__list-title cross-sell__list-title--pressable"
                onClick={() => {
                  trackClickCrossSellBack({ requestId });
                  navigate(`/my-requests/cross-sell/${requestId}`);
                }}>
                <FontIcon name="JP-Arrow-left-bold" size={24} />
                <h3 className="title-7 cross-sell__list-title">
                  {title ?? t("cross-sell.list.title")}
                </h3>
              </button>
            ) : (
              <h3 className="title-7 cross-sell__list-title">
                {title ?? t("cross-sell.list.title")}
              </h3>
            )}

            <div className="cross-sell__list-elements">
              {data.map((element) => (
                <RequestProposalCard
                  key={element.id}
                  testID={`cross-sell__list-element-${element.id}`}
                  media={
                    element.medias?.length > 0
                      ? getCloudinaryOptimizedUrl(element.medias[0].url, { contentType: "logo" })
                      : undefined
                  }
                  mediaAlt={element.medias?.[0]?.description_alt}
                  iconOverlay={serviceIcons[element.family] ?? "JP-Concierge-bell"}
                  title={element.title}
                  subtitle={element.description}
                  onClick={
                    element?.cross_sell_concierge?.sub_cross_sells?.length > 0
                      ? () => {
                          trackClickCrossSellGCCS({ requestId });
                          navigate(`/my-requests/cross-sell/${requestId}/${element.id}`);
                        }
                      : () => {
                          trackClickCrossSellCCS({
                            family: element.family,
                            subFamily: element.subfamily,
                            requestId,
                            contentId: element.id,
                          });
                          openBellLink(element.bell_deep_link);
                        }
                  }
                />
              ))}
            </div>
          </div>
          <div className="cross-sell__list-cancel-button">
            <Button
              label={t("cross-sell.list.cancel-button")}
              testID="cross-sell__list-cancel-button"
              onClick={() => {
                trackClickCrossSellLater({ requestId });
                onCancel?.();
              }}
              type="link"
            />
          </div>
        </div>
      )}
    </div>
  );
};
